import React, { useState, useMemo, useCallback } from 'react';
import Modal from 'pages/components/Modal/Modal';
import CustomButton from 'pages/components/CustomButton/CustomButton';
import HeroSection from '../components/HeroSection';
import DownloadComponent from '../components/DownloadComponent';
import WhereToBuy from 'pages/OurDrones/WhereToBuy/WhereToBuy';
import Certifications from 'pages/OurDrones/Certifications/Certifications';
import FeaturesTextSection from '../components/FeaturesTextSection';
import DroneSpecifications from 'pages/OurDrones/DroneSpecification/DroneSpecification';
import BVLOSOperations from 'pages/OurDrones/BVLOSOperations/BVLOSOperations';
import { useApi } from 'hooks/useApi';
import styles from './PDB.module.css';

const CMS_BASE_URL = process.env.REACT_APP_CMS_BASE_URL;

const PDB = () => {
  const [isWhereToBuyModalOpen, setIsWhereToBuyModalOpen] = useState(false);
  const [currentStoreData, setCurrentStoreData] = useState([]);

  const imagePaths = useMemo(() => ({
    pdbMain: '/Assets/Images/pdb/pdb-200a.svg',
    pdb200A: '/Assets/Images/pdb/pdb-200a.svg',
    pdb100A: '/Assets/Images/pdb/pdb-100a.svg',
    bvlosImage: '/Assets/Images/axlOrbit/bvlosGrid.svg'
  }), []);

  const { data: productData, loading: productLoading, error: productError } = useApi('/product-types', { params: { slug: 'pdb' } });
  const { data: productsResponse, loading: productsLoading, error: productsError } = useApi('/products', { params: { slug: 'pdb' } });

  const productsData = useMemo(() => {
    if (!productsResponse?.success || !productsResponse?.products) return [];
    return productsResponse.products;
  }, [productsResponse]);

  const transformFeatures = useCallback((features) => {
    if (!features || features.length === 0) return [];
    return features.map(feature => ({
      key: feature.title,
      value: feature.description || ''
    }));
  }, []);

  const transformSpecifications = useCallback((specifications) => {
    if (!specifications || specifications.length === 0) return [];
    return specifications.map(spec => ({
      label: spec.name,
      value: spec.value || ''
    }));
  }, []);

  const transformDesignedFor = useCallback((designedFor) => {
    if (!designedFor || designedFor.length === 0) return [];
    return designedFor.map(item => ({
      icon: item.icon ? item.icon : '',
      label: item.title,
      image: item.image ? item.image : imagePaths.bvlosImage
    }));
  }, [imagePaths.bvlosImage]);

  const getUniqueStoreData = useMemo(() => {
    const uniqueStores = new Map();
    productsData.forEach(product => {
      if (product.store && product.store.length > 0) {
        product.store.forEach(store => {
          uniqueStores.set(store.title, store);
        });
      }
    });
    return Array.from(uniqueStores.values());
  }, [productsData]);

  const product = useMemo(() => productData?.success ? productData.products : null, [productData]);

  const handleCloseWhereToBuyModal = useCallback(() => setIsWhereToBuyModalOpen(false), []);
  const handleOpenProductStoreModal = useCallback((storeData) => {
    setCurrentStoreData(storeData);
    setIsWhereToBuyModalOpen(true);
  }, []);

  const loading = productLoading || productsLoading;
  const error = productError || productsError;

  if (loading) return (
    <div className={styles.pageWrapper}>
      <div className={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className={styles.pageWrapper}>
      <div className={styles.errorContainer}>
        <p>Error: {error}</p>
      </div>
    </div>
  );

  return (
    <div className={styles.pageWrapper}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <HeroSection
          subtitle={product?.title || "PDB"}
          title={product?.banner_heading || "Engineered Stability for Demanding Systems"}
          gradientFrom="#F97738"
          gradientTo="#FCB999"
          showBottomFade={false}
          subtitleSize="clamp(6.5rem, 15vw, 18rem)"
        />
      </section>

      {/* PDB Overlap Section */}
      <div className={styles.pdbWrapper}>
        <img
          id="pdbImage"
          src={product?.banner_image ? `${CMS_BASE_URL}/attachments/${product.banner_image}` : imagePaths.pdbMain}
          alt="Power Distribution Board"
          className={styles.pdbImage}
          width="768"
          height="400"
          loading="eager"
        />
      </div>

      {/* Main Content */}
      <div className={styles.mainContainer}>
        {/* Description */}
        <section className={styles.descriptionSection}>
          <p>
            {product?.description || 
              "Designed to meet the demands of advanced aerial operations, our Power Distribution Board (PDB) is a cutting-edge component that seamlessly integrates with drone systems of up to octrocopter configuration, providing versatility and stability for your aerial platform. Featuring FMU PWM outputs, this board ensures precise and efficient control of your drone's motors through Flight Management Unit connectivity to ensure responsive and agile flight dynamics. This board also offers flexible input/output PWM options, which allow for customization and adaptability when connecting to various devices, further expanding your drone's versatility. With the capability to connect up to 8 Electronic Speed Controllers (ESCs), this board empowers you to individually manage each motor and thus optimize the performance and responsiveness of your drone system."
            }
          </p>
        </section>

        {/* Product Sections */}
        {productsData.map((productItem, index) => (
          <React.Fragment key={productItem.slug || index}>
            
            {/* Individual Product Section */}
            <section className={styles.productSection}>
              <h3 className={styles.productTitle}>{productItem.title}</h3>
              <p className={styles.productDescription}>{productItem.description}</p>
              
              <div className={styles.productContent}>
                <div className={styles.productImageWrapper}>
                  <img 
                    src={productItem.image ? `${CMS_BASE_URL}/attachments/${productItem.image}` : imagePaths.pdbMain} 
                    alt={productItem.title} 
                    className={styles.productImage}
                    loading="lazy"
                    width="320"
                    height="200"
                  />
                </div>
                <div className={styles.productFeatures}>
                  <FeaturesTextSection features={transformFeatures(productItem.features)} />
                </div>
              </div>
            </section>

            {/* Where to Buy Button */}
            {productItem.store && productItem.store.length > 0 && (
              <section className={styles.whereToBuySection}>
                <CustomButton
                  onClick={() => handleOpenProductStoreModal(productItem.store)}
                  size="large"
                  ariaLabel={`Where to Buy ${productItem.title}`}
                >
                  Where to Buy
                </CustomButton>
              </section>
            )}

            {/* BVLOS Operations */}
            {productItem.designed_for && productItem.designed_for.length > 0 && (
              <BVLOSOperations 
                operations={transformDesignedFor(productItem.designed_for)} 
                title={'Designed For'}
              />
            )}

            {/* Certifications */}
            <Certifications description={false}/>

            {/* Specifications and Downloads */}
            <div className={styles.specificationWrapper}>
              <DroneSpecifications 
                specifications={transformSpecifications(productItem.specification)}
              />
              
              <DownloadComponent 
                brochure={productItem.brochure ? `${CMS_BASE_URL}/attachments/${productItem.brochure}` : null}
              />
            </div>

          </React.Fragment>
        ))}

        {/* Where to Buy Modal */}
        <Modal isOpen={isWhereToBuyModalOpen} onClose={handleCloseWhereToBuyModal} title="Where to Buy" maxWidth="600px">
          <WhereToBuy storeData={currentStoreData} />
        </Modal>
      </div>
    </div>
  );
};

export default PDB;